import { useEffect, useState } from "react";
import { aimFind, clearFind, Finder, type FindHit } from "../find";
import { ficheNeedle } from "../ficheIdentity";
import { FicheMark } from "../FicheMark";
import { FicheDesk } from "../FicheDesk";
import { ThemeMenu } from "../theme/ThemeMenu";
import { RELATIONS } from "../Suivi";
import type { RelationStatus } from "../suivi";
import type { CrmFiche } from "../types";
import { alignWeekMode, blankWeekTask, kanbanBoardName, loadWeekTasks, rollPendingTasks, shareCaption, shareTone, taskDay, taskTimeLabel, watchWeek, type WeekTask } from "../week";
import { SpaceBoot } from "../SpaceBoot";
import { TaskSheet } from "../WeekBoard";
import { DayBlockButton } from "../DayBlock";
import { loadDayBlocks, type DayPart } from "../dayBlock";
import { GuideButton } from "../guide";
import { BookShareButton, FicheShareHint } from "../share";
import { ReceivedStrip } from "../outside";
import { PhoneFiche } from "./PhoneFiche";
import "./phone.css";

export function Phone({
  ready,
  error,
  fiches,
  visible,
  showArchived,
  onToggleArchived,
  kind,
  onKind,
  marks,
  due,
  fiche,
  saved,
  editing,
  onEdit,
  onSaved,
  onOpen,
  onClose,
  onChange,
  onStatus,
  onNew,
  onArchive,
  onErase,
  onRestore,
  onRetired,
}: {
  ready: boolean;
  error: string;
  fiches: CrmFiche[];
  visible: CrmFiche[];
  showArchived: boolean;
  onToggleArchived: () => void;
  kind: "" | RelationStatus;
  onKind: (value: "" | RelationStatus) => void;
  marks: Record<number, RelationStatus>;
  due: Record<number, number>;
  fiche: CrmFiche | null;
  saved: boolean;
  editing: boolean;
  onEdit: () => void;
  onSaved: () => void;
  onOpen: (fiche: CrmFiche) => void;
  onClose: () => void;
  onChange: (fiche: CrmFiche) => void;
  onStatus: (id: number, status: RelationStatus) => void;
  onNew: () => void;
  onArchive: (fiche: CrmFiche) => void;
  onErase: (fiche: CrmFiche) => void;
  onRestore: (id: number) => void;
  onRetired?: (fiche: CrmFiche, close?: boolean) => void;
}) {
  const [home, setHome] = useState<"today" | "fiches">("today");
  const [tasks, setTasks] = useState<WeekTask[]>([]);
  const [taskError, setTaskError] = useState("");
  const [sheet, setSheet] = useState<WeekTask | null>(null);
  const [day, setDay] = useState(localDay);
  const [query, setQuery] = useState("");
  const [seek, setSeek] = useState(false);
  const [booting, setBooting] = useState(true);

  useEffect(() => {
    let alive = true;
    const pull = () => {
      void alignWeekMode()
        .catch(() => undefined)
        .then(() => loadWeekTasks())
        .then((rows) => {
          if (!alive) return;
          setTasks(rows);
          setTaskError("");
          setBooting(false);
          return rollPendingTasks().catch(() => 0);
        })
        .then((moved) => {
          if (!alive || !moved) return undefined;
          return loadWeekTasks();
        })
        .then((rows) => {
          if (!alive || !rows) return;
          setTasks(rows);
        })
        .catch((reason: unknown) => {
          if (alive) setTaskError(reason instanceof Error ? reason.message : "Tâches indisponibles.");
        })
        .finally(() => {
          if (alive) setBooting(false);
        });
    };
    pull();
    const stop = watchWeek(pull);
    return () => {
      alive = false;
      stop();
    };
  }, []);

  function go(next: "today" | "fiches") {
    setHome(next);
    onClose();
    if (next === "today") setDay(localDay());
  }

  function draftFor(row?: CrmFiche): WeekTask {
    const task = blankWeekTask(day);
    if (!row) return task;
    return { ...task, client_name: row.name, meta: { ...task.meta, crmFicheId: row.id } };
  }

  const today = localDay();
  const needle = query.trim().toLowerCase();
  const listed = needle === "" ? visible : visible.filter((row) => ficheNeedle(row).includes(needle));

  function openHit(hit: FindHit) {
    setQuery("");
    setSeek(false);
    if (hit.kind === "Tâche") {
      const task = tasks.find((row) => row.id === hit.taskId);
      if (task) {
        setHome("today");
        setSheet(task);
      }
      return;
    }
    const row = fiches.find((item) => item.id === hit.ficheId);
    if (!row) return;
    if (hit.kind === "Fiche") clearFind();
    else aimFind(hit);
    onOpen(row);
  }

  return (
    <div className="crm-app crm-phone flex flex-col">
      {fiche && saved ? (
        <PhoneFiche
          fiche={fiche}
          tone={marks[fiche.id] || "prospect"}
          editing={editing}
          archived={showArchived}
          onBack={onClose}
          onEdit={onEdit}
          onSaved={onSaved}
          onChange={onChange}
          onStatus={(status) => onStatus(fiche.id, status)}
          onArchive={() => onArchive(fiche)}
          onErase={() => onErase(fiche)}
          onRestore={() => onRestore(fiche.id)}
          onNewTask={() => setSheet(draftFor(fiche))}
          others={fiches}
          onRetired={onRetired}
        />
      ) : fiche ? (
        <div className="crm-phone-main">
          <button type="button" className="mb-3 text-sm font-semibold crm-ink" onClick={onClose}>Retour</button>
          <FicheDesk fiche={fiche} others={fiches} onChange={onChange} onSaved={onSaved} onOpen={onOpen} onRetired={onRetired} />
        </div>
      ) : (
        <>
          <header className="crm-phone-bar crm-surface shrink-0 border-b border-slate-200 px-3 pb-3 pt-3">
            <div className="flex items-center gap-2">
              <p className="min-w-0 flex-1 truncate text-base font-semibold tracking-tight">Aurélia CRM</p>
              <GuideButton />
              <ThemeMenu />
            </div>
            <div className="relative mt-2">
              <input className="crm-seek h-9 w-full rounded-full border px-3 text-sm" value={query} placeholder="Fiche, tâche, note…" onChange={(event) => { setQuery(event.target.value); setSeek(true); }} onFocus={() => setSeek(true)} onBlur={() => setSeek(false)} />
              {seek && query.trim().length >= 2 ? <Finder query={query} fiches={fiches} onPick={openHit} /> : null}
            </div>
          </header>
          <main className={!ready || booting ? "crm-phone-main flex flex-col" : "crm-phone-main"}>
            {!ready || booting ? <SpaceBoot /> : null}
            {error && ready && !booting ? <p className="mb-3 text-sm text-rose-700">{error}</p> : null}
            {taskError && ready && !booting ? <p className="mb-3 text-sm text-rose-700">{taskError}</p> : null}
            {ready && !booting ? <ReceivedStrip /> : null}
            {ready && !booting && home === "today" ? (
              <Today ready={ready} day={day} today={today} tasks={tasks} fiches={fiches} onOpenTask={setSheet} onCreate={() => setSheet(draftFor())} onShift={(delta) => setDay((current) => shiftDay(current, delta))} onToday={() => setDay(today)} />
            ) : ready && !booting ? (
              <FicheList
                ready={ready}
                visible={listed}
                showArchived={showArchived}
                kind={kind}
                marks={marks}
                due={due}
                empty={fiches.length === 0}
                onKind={onKind}
                onToggleArchived={onToggleArchived}
                onOpen={onOpen}
                onNew={onNew}
                onArchive={onArchive}
                onErase={onErase}
                onRestore={onRestore}
              />
            ) : null}
          </main>
        </>
      )}
      <nav className="crm-phone-nav crm-surface" aria-label="Navigation">
        <button type="button" className={home === "today" && !fiche ? "text-sm font-semibold crm-ink" : "text-sm font-semibold text-slate-500"} onClick={() => go("today")}>Aujourd’hui</button>
        <button type="button" className={home === "fiches" && !fiche ? "text-sm font-semibold crm-ink" : "text-sm font-semibold text-slate-500"} onClick={() => go("fiches")}>Fiches</button>
      </nav>
      {sheet ? (
        <TaskSheet
          phone
          task={sheet}
          fiches={fiches}
          onClose={() => setSheet(null)}
          onOpenFiche={(id) => {
            const row = fiches.find((item) => item.id === id);
            setSheet(null);
            if (row) onOpen(row);
          }}
          onSaved={async () => { setSheet(null); }}
        />
      ) : null}
    </div>
  );
}

function Today({ ready, day, today, tasks, fiches, onOpenTask, onCreate, onShift, onToday }: { ready: boolean; day: string; today: string; tasks: WeekTask[]; fiches: CrmFiche[]; onOpenTask: (task: WeekTask) => void; onCreate: () => void; onShift: (delta: number) => void; onToday: () => void }) {
  const title = dayDate(day).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
  const [heldBlocks, setHeldBlocks] = useState<Record<string, DayPart[]>>({});
  const [blockLinked, setBlockLinked] = useState(true);
  const [blockReady, setBlockReady] = useState(true);
  const blockTo = shiftDay(day, 3);
  useEffect(() => {
    let alive = true;
    void loadDayBlocks(day, blockTo)
      .then((held) => {
        if (!alive) return;
        setHeldBlocks(held.days);
        setBlockLinked(held.linked);
        setBlockReady(held.ready);
      })
      .catch(() => undefined);
    return () => { alive = false; };
  }, [day, blockTo]);
  function paintBlocks(stamp: string, parts: DayPart[], linked: boolean, able: boolean) {
    setHeldBlocks((current) => ({ ...current, [stamp]: parts }));
    setBlockLinked(linked);
    setBlockReady(able);
  }
  const word = day === today ? "Aujourd’hui" : day === shiftDay(today, -1) ? "Hier" : day === shiftDay(today, 1) ? "Demain" : "";
  const blocks = [0, 1, 2, 3].map((offset) => {
    const key = shiftDay(day, offset);
    const rows = tasks
      .filter((task) => taskDay(task) === key)
      .sort((a, b) => taskTimeLabel(a).localeCompare(taskTimeLabel(b), "fr"));
    return {
      key,
      open: rows.filter((task) => task.status !== "done"),
      done: rows.filter((task) => task.status === "done"),
    };
  });
  const later = blocks.slice(1).some((block) => block.open.length > 0 || block.done.length > 0);
  return (
    <section>
      <div className="flex items-center gap-1">
        <button type="button" className="grid h-11 w-11 shrink-0 place-items-center rounded-full text-lg" aria-label="Jour précédent" onClick={() => onShift(-1)}>‹</button>
        <div className="min-w-0 flex-1 text-center">
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">{word || "Jour"}</p>
          <div className="flex items-center justify-center gap-1">
            <h2 className="crm-title truncate text-lg font-semibold capitalize">{title}</h2>
            <DayBlockButton day={day} label={title} parts={heldBlocks[day] || []} linked={blockLinked} ready={blockReady} onChange={paintBlocks} />
          </div>
        </div>
        <button type="button" className="grid h-11 w-11 shrink-0 place-items-center rounded-full text-lg" aria-label="Jour suivant" onClick={() => onShift(1)}>›</button>
      </div>
      <div className="mt-3 flex items-center justify-between gap-2">
        {day === today ? <span /> : <button type="button" className="rounded-full border border-slate-200 px-3 py-2 text-sm font-semibold" onClick={onToday}>Aujourd’hui</button>}
        <button type="button" className="ml-auto rounded-full crm-fill px-3 py-2 text-sm font-semibold text-white" onClick={onCreate}>+ Tâche</button>
      </div>
      {blocks.map((block, index) => {
        const empty = block.open.length === 0 && block.done.length === 0;
        if (empty && index > 0) return null;
        const heading = dayDate(block.key).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
        return (
          <div key={block.key}>
            {index > 0 ? (
              <div className="mt-6 flex items-center gap-1">
                <h3 className="crm-title text-base font-semibold capitalize">{heading}</h3>
                <DayBlockButton day={block.key} label={heading} parts={heldBlocks[block.key] || []} linked={blockLinked} ready={blockReady} onChange={paintBlocks} />
              </div>
            ) : null}
            {ready && index === 0 && empty ? <p className="mt-4 text-sm text-slate-500">{later ? "Rien ce jour-ci. La suite est juste en dessous." : day === today ? "Rien de prévu aujourd’hui." : "Rien de prévu ce jour."}</p> : null}
            {block.open.length > 0 ? (
              <ul className="mt-4 space-y-2">
                {block.open.map((task) => <TaskRow key={task.id} task={task} fiches={fiches} onOpen={onOpenTask} />)}
              </ul>
            ) : null}
            {block.done.length > 0 ? (
              <>
                <p className="mt-6 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Faites</p>
                <ul className="mt-2 space-y-2">
                  {block.done.map((task) => <TaskRow key={task.id} task={task} fiches={fiches} onOpen={onOpenTask} />)}
                </ul>
              </>
            ) : null}
          </div>
        );
      })}
    </section>
  );
}

function TaskRow({ task, fiches, onOpen }: { task: WeekTask; fiches: CrmFiche[]; onOpen: (task: WeekTask) => void }) {
  const fiche = matchFiche(fiches, task);
  const closed = task.status === "done";
  return (
    <li>
      <button type="button" className="crm-card flex w-full items-center gap-3 rounded-2xl border border-slate-200 px-3 py-3 text-left" onClick={() => onOpen(task)}>
        <span className="w-14 shrink-0 text-xs font-semibold text-slate-500">{taskTimeLabel(task) || "Journée"}</span>
        <span className="min-w-0">
          <span className={closed ? "block truncate text-sm font-semibold text-slate-400 line-through" : "block truncate text-sm font-semibold"}>{task.title || "Sans titre"}</span>
          <span className="block truncate text-xs text-slate-500">{[fiche?.name || task.client_name || "Sans fiche", kanbanBoardName(task.meta) ? `Kanban · ${kanbanBoardName(task.meta)}` : ""].filter(Boolean).join(" · ")}</span>
          {shareCaption(task) ? <span className={shareTone(task) === "in" ? "block truncate text-[11px] font-medium text-violet-700" : "block truncate text-[11px] font-medium text-sky-700"}>{shareCaption(task)}</span> : null}
        </span>
      </button>
    </li>
  );
}

function FicheList({
  ready,
  visible,
  showArchived,
  kind,
  marks,
  due,
  empty,
  onKind,
  onToggleArchived,
  onOpen,
  onNew,
  onArchive,
  onErase,
  onRestore,
}: {
  ready: boolean;
  visible: CrmFiche[];
  showArchived: boolean;
  kind: "" | RelationStatus;
  marks: Record<number, RelationStatus>;
  due: Record<number, number>;
  empty: boolean;
  onKind: (value: "" | RelationStatus) => void;
  onToggleArchived: () => void;
  onOpen: (fiche: CrmFiche) => void;
  onNew: () => void;
  onArchive: (fiche: CrmFiche) => void;
  onErase: (fiche: CrmFiche) => void;
  onRestore: (id: number) => void;
}) {
  return (
    <section>
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-xl font-semibold tracking-tight">{showArchived ? "Archives" : "Fiches"}</h2>
        <div className="flex items-center gap-2">
          <BookShareButton compact />
          <button type="button" className="rounded-full crm-fill px-3 py-2 text-sm font-semibold text-white" onClick={onNew}>+ Fiche</button>
        </div>
      </div>
      <div className="crm-phone-tabs mt-3">
        {([["", "Tous"], ["prospect", "Prospect"], ["client", "Client"], ["former", "Ancien"]] as const).map(([value, label]) => (
          <button key={value || "all"} type="button" className={kind === value ? "crm-fill rounded-full px-3 py-1.5 text-xs font-semibold text-white" : "crm-pill rounded-full px-3 py-1.5 text-xs font-semibold"} onClick={() => onKind(value)}>{label}</button>
        ))}
      </div>
      <button type="button" className="mt-3 text-xs font-semibold text-slate-500" onClick={onToggleArchived}>{showArchived ? "Voir les fiches actives" : "Voir les archives"}</button>
      {ready && empty && !showArchived ? (
        <div className="crm-card mt-4 rounded-2xl border border-slate-200 p-4">
          <p className="text-sm font-semibold">CRM clients</p>
          <p className="mt-1 text-sm text-slate-500">Une fiche crée son dossier dans CRM clients.</p>
          <button type="button" className="mt-3 rounded-full crm-fill px-4 py-2 text-sm font-semibold text-white" onClick={onNew}>Nouvelle fiche</button>
        </div>
      ) : null}
      {ready && !empty && visible.length === 0 ? <p className="mt-4 text-sm text-slate-500">{showArchived ? "Aucune fiche archivée." : "Aucune fiche pour ce filtre."}</p> : null}
      <ul className="mt-3 space-y-2">
        {visible.map((row) => {
          const count = due[row.id] || 0;
          const tone = marks[row.id] || "prospect";
          const label = RELATIONS.find((item) => item.value === tone)?.label || "Prospect";
          return (
            <li key={row.id} className="crm-card flex items-center gap-2 rounded-2xl border border-slate-200 py-2 pl-3 pr-2">
              <button type="button" className="flex min-w-0 flex-1 items-center gap-3 py-1 text-left" onClick={() => onOpen(row)}>
                <span className="relative shrink-0">
                  <FicheMark path={row.logo_path} name={row.name} tone={tone} className="h-10 w-10 text-xs" />
                  {count > 0 ? <span className="crm-day-dot">{count > 9 ? "9+" : count}</span> : null}
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-sm font-semibold">{row.name}</span>
                  <FicheShareHint fiche={row} />
                  <span className="block truncate text-xs text-slate-500">{[label, row.city || row.email].filter(Boolean).join(" · ")}</span>
                </span>
              </button>
              {showArchived ? (
                <>
                  <button type="button" className="shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold text-rose-600" onClick={() => onErase(row)}>Suppr</button>
                  <button type="button" className="crm-pill shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold" onClick={() => onRestore(row.id)}>OK</button>
                </>
              ) : (
                <button type="button" className="grid h-10 w-10 shrink-0 place-items-center rounded-full text-slate-500" aria-label={`Archiver ${row.name}`} onClick={() => onArchive(row)}>
                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 7h16M9 7V5.5A1.5 1.5 0 0 1 10.5 4h3A1.5 1.5 0 0 1 15 5.5V7M6.5 7l.7 12.2A1.5 1.5 0 0 0 8.7 20.5h6.6a1.5 1.5 0 0 0 1.5-1.3L17.5 7" />
                  </svg>
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function matchFiche(fiches: CrmFiche[], task: WeekTask): CrmFiche | undefined {
  const id = Number(task.meta.crmFicheId);
  if (id > 0) {
    const row = fiches.find((fiche) => fiche.id === id);
    if (row) return row;
  }
  const key = task.client_name.trim().toLocaleLowerCase("fr");
  if (!key) return undefined;
  return fiches.find((row) => row.name.trim().toLocaleLowerCase("fr") === key);
}

function localDay(date = new Date()): string {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

function shiftDay(stamp: string, delta: number): string {
  const date = dayDate(stamp);
  date.setDate(date.getDate() + delta);
  return localDay(date);
}

function dayDate(stamp: string): Date {
  const [year, month, day] = stamp.split("-").map(Number);
  return new Date(year || 1970, (month || 1) - 1, day || 1);
}
